/**
 * Where a voice-note transcript lands in the caption.
 *
 * Same rule as the app (`src/utils/transcriptPlacement.ts`), keep them in
 * parity:
 *
 * 1. The editor chose a spot: a caret or selection in the caption textarea
 *    that still fits the current text. The transcript replaces the
 *    selection (or is inserted at the caret) with the spacing fixed up.
 * 2. Otherwise the persons slot, located from the caption FRAME: the
 *    transcript replaces whatever sits between the frame's prefix and
 *    suffix. This works when that phrase is EMPTY, which is the common case
 *    on a frame with nobody in it (field-found 16-09-2026: a stadium shot
 *    with no people appended the transcript to the very end, because the
 *    only marker the code had was a phrase that did not exist).
 * 3. Otherwise the persons phrase by text search, for payloads with no frame.
 * 4. Otherwise appended with one space.
 *
 * Pure string work: the caller passes the persons phrase it already knows.
 * The caption is never rendered from the template here.
 */

function validSelection(sel, length) {
  if (!sel) return false;
  const { start, end } = sel;
  if (!Number.isInteger(start) || !Number.isInteger(end)) return false;
  return start >= 0 && end >= start && end <= length;
}

/** How much punctuation collapse is tolerated at a frame boundary. */
const BOUNDARY_SLACK = 3;

function commonPrefixLength(a, b) {
  const max = Math.min(a.length, b.length);
  let i = 0;
  while (i < max && a[i] === b[i]) i += 1;
  return i;
}

function commonSuffixLength(a, b) {
  const max = Math.min(a.length, b.length);
  let i = 0;
  while (i < max && a[a.length - 1 - i] === b[b.length - 1 - i]) i += 1;
  return i;
}

/**
 * Where the persons phrase sits in this caption, empty or not.
 *
 * `spliceCaption` post-processes `prefix + phrase + suffix`, and for an EMPTY
 * phrase that collapses a separator, so the caption is not always a literal
 * concatenation. An exact match is tried first; otherwise the boundaries come
 * from how far the strings still agree, and only within a few characters, so
 * an edited caption is left alone.
 */
function slotFromFrame(caption, frame) {
  if (!frame) return null;
  const prefix = frame.prefix ?? '';
  const suffix = frame.suffix ?? '';
  if (!prefix && !suffix) return null;

  if (
    caption.startsWith(prefix) &&
    caption.endsWith(suffix) &&
    prefix.length + suffix.length <= caption.length
  ) {
    return { start: prefix.length, end: caption.length - suffix.length };
  }

  const start = commonPrefixLength(caption, prefix);
  const matchedSuffix = commonSuffixLength(caption, suffix);
  const end = caption.length - matchedSuffix;

  if (prefix.length - start > BOUNDARY_SLACK) return null;
  if (suffix.length - matchedSuffix > BOUNDARY_SLACK) return null;
  if (end > caption.length) return null;

  // With an empty phrase the prefix's trailing separator and the suffix's
  // leading one collapse into one character, so the matched regions OVERLAP.
  // The slot is then the zero-width point between them.
  if (end < start) {
    if (start - end > BOUNDARY_SLACK) return null;
    return { start: end, end };
  }

  return { start, end };
}

function insertAt(caption, start, end, text) {
  const before = caption.slice(0, start);
  const after = caption.slice(end);
  const needsSpaceBefore = before.length > 0 && !/\s$/.test(before);
  const needsSpaceAfter = after.length > 0 && !/^[\s.,;:!?)]/.test(after);
  return `${before}${needsSpaceBefore ? ' ' : ''}${text}${needsSpaceAfter ? ' ' : ''}${after}`;
}

/**
 * @param {string} caption
 * @param {string} transcript
 * @param {{ selection?: {start:number,end:number}|null, personsPhrase?: string|null, frame?: {prefix:string,suffix:string}|null }} [options]
 * @returns {{ caption: string, mode: 'caret'|'slot'|'append'|'none' }}
 */
export function placeTranscript(caption, transcript, options = {}) {
  const text = (transcript ?? '').trim();
  const base = caption ?? '';
  if (!text) return { caption: base, mode: 'none' };

  const sel = options.selection ?? null;
  if (validSelection(sel, base.length)) {
    return { caption: insertAt(base, sel.start, sel.end, text), mode: 'caret' };
  }

  const slot = slotFromFrame(base, options.frame);
  if (slot) {
    return { caption: insertAt(base, slot.start, slot.end, text), mode: 'slot' };
  }

  const phrase = (options.personsPhrase ?? '').trim();
  if (phrase) {
    const at = base.indexOf(phrase);
    if (at >= 0) {
      return { caption: insertAt(base, at, at + phrase.length, text), mode: 'slot' };
    }
  }

  if (!base.trim()) return { caption: text, mode: 'append' };
  return { caption: `${base.replace(/\s+$/, '')} ${text}`, mode: 'append' };
}
